import { IEntityHashMap } from 'flux-entities'

import { IPlan } from '../../../../types/plan'

export interface IModelWithEntitlements {
  name: string
  module: string
  entitlements: number
}

export interface IModelWithoutEntitlements {
  name: string
  module: string
}

export type TModelsWithEntitlements = IEntityHashMap<IModelWithEntitlements[]>

export interface IGroupedModels {
  firstGroup: TModelsWithEntitlements
  secondGroup: TModelsWithEntitlements
  unvalidatedGroup: IModelWithEntitlements[]
}

/**
 * Formats a plan for rendering in the show modal.
 *
 * @param {IPlan} the plan
 * @param {String[]} array of drug modules. Eg ['DoseMe::Drugs::Digoxin', 'DoseMe::Drugs::Vancomycin']
 * @returns {IGroupedModels} drugs in two groups. A-M and N-Z.
 */
const groupModels = (plan: IPlan, drugs: IModelWithoutEntitlements[]): IGroupedModels => {
  const firstGroup: IEntityHashMap<IModelWithEntitlements[]> = {}
  const secondGroup: IEntityHashMap<IModelWithEntitlements[]> = {}
  let unvalidatedGroup: IModelWithEntitlements[] = []
  const allDrugs: string[] = []

  const drugsMap: IEntityHashMap<IModelWithEntitlements[]> = {}

  // DoseMe::Drugs::Vancomycin::Goti -> Vancomyin:Goti
  const removeDoseMeNamespace = (str: string) => str.replace('DoseMe::Drugs::', '')
  // Unvalidated::Dabigatran ->  Unv::Dabigatran
  const shorten = (str: string) => str.replace('Unvalidated', 'Unv')

  for (const model of drugs) {
    const drugName = removeDoseMeNamespace(model.module).split(':')[0]
    if (!drugsMap[drugName]) {
      allDrugs.push(drugName)
      drugsMap[drugName] = []
    }
    const drugInPlan = plan.drugs.find(x => x.module === model.module)

    drugsMap[drugName].push({
      name: model.name,
      module: shorten(removeDoseMeNamespace(model.module)),
      entitlements: drugInPlan?.maxCourseCount || 0
    })
  }

  const alphabetical = (x: IModelWithEntitlements, y: IModelWithEntitlements): number => x.module < y.module ? -1 : 1

  for (const key of Object.keys(drugsMap)) {
    if (key === 'Unvalidated') {
      unvalidatedGroup = drugsMap[key]
    } else if (key[0] < 'N') {
      firstGroup[key] = drugsMap[key].sort(alphabetical)
    } else {
      secondGroup[key] = drugsMap[key].sort(alphabetical)
    }
  }

  return {
    firstGroup,
    secondGroup,
    unvalidatedGroup,
  }
}

export {
  groupModels,
}
